/**
 * Footsteps, gear and landings.
 *
 * A step is two events, not one: a HEEL strike (low, material-coloured contact)
 * and a TOE roll-off 40–90 ms later (brighter scuff, longer, quieter). Gait scales
 * the impact energy, the heel–toe gap and the amount of gear rattle riding on top.
 * Every parameter is jittered per step from the seeded RNG so a sprint never turns
 * into a metronome.
 */
import {
  modal, applyExpDecay, applyAD, bandpass, lowpass, highpass, sineSweep,
  granular, normalise as normArr,
} from '../synth/dsp.js';
import { noiseSlice } from '../synth/noise.js';
import { bq, gain, playData } from '../synth/nodes.js';
import { HANDLING } from './Handling.js';

function emit(ac, dest, t0, data, g, filters = [], rate = 1) {
  const gn = gain(ac, g);
  let head = gn;
  for (let i = filters.length - 1; i >= 0; i--) { filters[i].connect(head); head = filters[i]; }
  gn.connect(dest);
  playData(ac, data, head, Math.max(0, t0), { rate });
  return gn;
}

const j = (rng, a = 0.1) => 1 + (rng() * 2 - 1) * a;

/**
 * Per-surface footstep voicing.
 *  heelF   : low contact frequency
 *  res     : material modes [ratio, amp, decay]
 *  scuffF  : toe scuff band centre
 *  grit    : granular particulate amount (gravel, sand, glass)
 */
export const STEP_SURFACES = {
  concrete: {
    level: 0.55, heelF: 165, heelF1: 68, heelTau: 0.026, heelLevel: 0.75,
    res: 620, modes: [[1, 1, 0.012], [2.1, 0.35, 0.007]], resLevel: 0.30,
    scuffF: 2600, scuffQ: 0.7, scuffTau: 0.055, scuffLevel: 0.45, grit: 0.15, gritF: 2400, hp: 90,
  },
  metal: {
    level: 0.60, heelF: 200, heelF1: 90, heelTau: 0.020, heelLevel: 0.55,
    res: 1150, modes: [[1, 1, 0.11], [2.37, 0.6, 0.07], [3.9, 0.35, 0.04], [0.62, 0.5, 0.16]], resLevel: 0.60,
    scuffF: 3600, scuffQ: 0.9, scuffTau: 0.045, scuffLevel: 0.40, grit: 0.06, gritF: 3600, hp: 160,
  },
  wood: {
    level: 0.55, heelF: 175, heelF1: 74, heelTau: 0.030, heelLevel: 0.70,
    res: 300, modes: [[1, 1, 0.045], [2.05, 0.45, 0.028], [3.3, 0.2, 0.016], [0.62, 0.45, 0.07]], resLevel: 0.55,
    scuffF: 1800, scuffQ: 0.7, scuffTau: 0.050, scuffLevel: 0.38, grit: 0.05, gritF: 1600, hp: 70,
  },
  dirt: {
    level: 0.45, heelF: 130, heelF1: 52, heelTau: 0.034, heelLevel: 0.72,
    res: 220, modes: [[1, 1, 0.010]], resLevel: 0.12,
    scuffF: 900, scuffQ: 0.5, scuffTau: 0.075, scuffLevel: 0.50, grit: 0.45, gritF: 1200, hp: 55,
  },
  sand: {
    level: 0.38, heelF: 110, heelF1: 46, heelTau: 0.028, heelLevel: 0.45,
    res: 200, modes: [[1, 1, 0.006]], resLevel: 0.05,
    scuffF: 3400, scuffQ: 0.4, scuffTau: 0.10, scuffLevel: 0.70, grit: 0.70, gritF: 4200, hp: 200,
  },
  glass: {
    level: 0.55, heelF: 190, heelF1: 82, heelTau: 0.018, heelLevel: 0.40,
    res: 2800, modes: [[1, 1, 0.030], [1.7, 0.6, 0.020], [2.9, 0.35, 0.012]], resLevel: 0.45,
    scuffF: 5200, scuffQ: 0.9, scuffTau: 0.070, scuffLevel: 0.55, grit: 0.85, gritF: 6000, hp: 500,
  },
  fabric: {
    level: 0.30, heelF: 120, heelF1: 55, heelTau: 0.030, heelLevel: 0.55,
    res: 200, modes: [[1, 1, 0.008]], resLevel: 0.08,
    scuffF: 1400, scuffQ: 0.5, scuffTau: 0.060, scuffLevel: 0.40, grit: 0.10, gritF: 1500, hp: 80,
  },
  water: {
    level: 0.55, heelF: 100, heelF1: 190, heelTau: 0.030, heelLevel: 0.45, heelUp: true,
    res: 600, modes: [[1, 1, 0.015]], resLevel: 0.15,
    scuffF: 2400, scuffQ: 0.5, scuffTau: 0.14, scuffLevel: 0.75, grit: 0.20, gritF: 3000, hp: 140, splash: true,
  },
  flesh: {
    level: 0.40, heelF: 140, heelF1: 60, heelTau: 0.032, heelLevel: 0.70,
    res: 300, modes: [[1, 1, 0.010]], resLevel: 0.15,
    scuffF: 800, scuffQ: 1.1, scuffTau: 0.045, scuffLevel: 0.35, grit: 0.05, gritF: 900, hp: 70,
  },
  foliage: {
    level: 0.34, heelF: 150, heelF1: 70, heelTau: 0.015, heelLevel: 0.25,
    res: 900, modes: [[1, 1, 0.006]], resLevel: 0.08,
    scuffF: 4000, scuffQ: 0.5, scuffTau: 0.13, scuffLevel: 0.80, grit: 0.60, gritF: 4500, hp: 500,
  },
};

/** Gait scaling: [energy, heel-toe gap seconds, gear amount, brightness]. */
export const GAITS = {
  crouch: { energy: 0.30, gap: 0.085, gear: 0.25, bright: 0.75 },
  walk: { energy: 0.62, gap: 0.070, gear: 0.55, bright: 0.95 },
  idle: { energy: 0.55, gap: 0.070, gear: 0.45, bright: 0.9 },
  run: { energy: 1.00, gap: 0.048, gear: 1.00, bright: 1.10 },
  sprint: { energy: 1.25, gap: 0.038, gear: 1.35, bright: 1.20 },
  slide: { energy: 0.85, gap: 0.11, gear: 1.10, bright: 0.9 },
  air: { energy: 0.0, gap: 0.05, gear: 0.3, bright: 1.0 },
  mantle: { energy: 0.7, gap: 0.09, gear: 1.2, bright: 0.95 },
};

export function stepSpec(surface) {
  return STEP_SURFACES[surface] || STEP_SURFACES.concrete;
}
export function gaitSpec(gait) {
  return GAITS[gait] || GAITS.walk;
}

/**
 * One footstep. `foot` ('l' | 'r' | 0 | 1) shifts the voicing slightly so
 * alternating feet are audibly different.
 */
export function buildFootstep(ac, dest, t0, rng, surface = 'concrete', opts = {}) {
  const S = stepSpec(surface);
  const G = gaitSpec(opts.gait);
  const sr = ac.sampleRate;
  const e = G.energy * (opts.energy ?? 1);
  if (e <= 0.001) return { duration: 0.05 };
  const v = (opts.volume ?? 1) * S.level * e;
  const footBias = (opts.foot === 'r' || opts.foot === 1) ? 1.045 : 0.965;
  const pitch = j(rng, 0.07) * footBias;
  const gap = G.gap * j(rng, 0.22);

  /* ---- heel strike ---- */
  {
    const f0 = S.heelUp ? S.heelF : S.heelF * pitch;
    const f1 = S.heelF1 * pitch;
    const d = sineSweep(sr, Math.min(0.35, S.heelTau * 9), f0, f1, S.heelTau, rng, 2.2);
    emit(ac, dest, t0, d, v * S.heelLevel, [bq(ac, 'lowpass', 380, 0.9)]);

    const n = Math.floor(sr * 0.05);
    const cd = noiseSlice('white', n, rng);
    applyExpDecay(cd, sr, 0.0022);
    highpass(cd, sr, 900 * G.bright, 0.7);
    normArr(cd, 1);
    emit(ac, dest, t0, cd, v * 0.35 * G.bright);
  }

  /* ---- material resonance ---- */
  if (S.resLevel > 0.02) {
    const base = S.res * pitch;
    const modes = S.modes.map(([m, a, tau]) => [base * m, a, tau]);
    const d = modal(sr, Math.min(0.5, S.modes[0][2] * 6 + 0.05), modes, rng, 0.06);
    normArr(d, 1);
    emit(ac, dest, t0 + 0.001, d, v * S.resLevel, [bq(ac, 'highpass', S.hp, 0.7)]);
  }

  /* ---- toe scuff ---- */
  {
    const n = Math.floor(sr * Math.min(0.4, S.scuffTau * 6));
    const d = noiseSlice('white', n, rng);
    bandpass(d, sr, S.scuffF * G.bright * j(rng, 0.12), S.scuffQ);
    applyAD(d, sr, 0.006, S.scuffTau);
    normArr(d, 1);
    emit(ac, dest, t0 + gap, d, v * S.scuffLevel * 0.75, [bq(ac, 'highpass', S.hp, 0.7)]);
  }

  /* ---- grit / particulate ---- */
  if (S.grit > 0.02) {
    const src = noiseSlice('white', Math.floor(sr * 0.02), rng);
    bandpass(src, sr, S.gritF, 1.4);
    applyExpDecay(src, sr, 0.004);
    const d = granular(sr, 0.22, src, rng, {
      grains: Math.round(8 + 26 * S.grit), grainMs: 9, spread: 0.7, pitchJitter: 0.6, gainJitter: 0.85,
    });
    for (let i = 0; i < d.length; i++) d[i] *= Math.exp(-(i / sr) / 0.06);
    normArr(d, 1);
    emit(ac, dest, t0 + gap * 0.4, d, v * S.grit * 0.55, [bq(ac, 'highpass', 800, 0.7)]);
  }

  /* ---- water splash ---- */
  if (S.splash) {
    for (let i = 0; i < 3; i++) {
      const f = 400 + rng() * 1200;
      const d = sineSweep(sr, 0.06, f * 0.6, f, 0.015, rng, 1.5);
      emit(ac, dest, t0 + 0.01 + rng() * 0.10, d, v * 0.14 * (0.4 + rng()), [bq(ac, 'bandpass', f, 2)]);
    }
  }

  /* ---- gear rattle, scaled by gait ---- */
  if (G.gear > 0.05 && (opts.gear ?? 1) > 0) {
    HANDLING.gear(ac, dest, t0 + 0.004 + rng() * 0.02, rng, { volume: 0.5 * G.gear * (opts.gear ?? 1) * (opts.volume ?? 1) });
  }

  return { duration: 0.42 };
}

/**
 * Landing thump. `impact` is the downward speed in m/s (or a 0..1 blend if the
 * player team hands us a normalised value — both are handled).
 */
export function buildLanding(ac, dest, t0, rng, surface = 'concrete', opts = {}) {
  const S = stepSpec(surface);
  const sr = ac.sampleRate;
  const raw = opts.impact ?? 4;
  const speed = raw <= 1.5 ? raw * 8 : raw;              // accept 0..1 or m/s
  const k = Math.max(0.15, Math.min(1.4, speed / 9));
  // capped so even a maximum-velocity landing leaves headroom before the limiter
  const v = (opts.volume ?? 1) * S.level * Math.min(1.35, 0.55 + 1.15 * k);
  const pitch = j(rng, 0.06);

  // deep body thump — the chest hit
  {
    const d = sineSweep(sr, 0.42, (S.heelF * 1.15) * pitch, S.heelF1 * 0.72 * pitch, 0.055 + 0.05 * k, rng, 2);
    emit(ac, dest, t0, d, v * 0.78, [bq(ac, 'lowpass', 300, 0.95)]);
  }
  // boot slap
  {
    const n = Math.floor(sr * 0.14);
    const d = noiseSlice('white', n, rng);
    bandpass(d, sr, S.scuffF * 0.7, 0.8);
    applyAD(d, sr, 0.0018, 0.030 + 0.02 * k);
    normArr(d, 1);
    emit(ac, dest, t0, d, v * 0.42, [bq(ac, 'highpass', S.hp, 0.7)]);
  }
  // material ring
  if (S.resLevel > 0.02) {
    const base = S.res * pitch;
    const modes = S.modes.map(([m, a, tau]) => [base * m, a, tau * 1.4]);
    const d = modal(sr, Math.min(0.6, S.modes[0][2] * 8 + 0.06), modes, rng, 0.05);
    normArr(d, 1);
    emit(ac, dest, t0 + 0.001, d, v * S.resLevel * 0.6, [bq(ac, 'highpass', S.hp, 0.7)]);
  }
  // grit spray + a heavy gear crash
  if (S.grit > 0.02) {
    const src = noiseSlice('white', Math.floor(sr * 0.02), rng);
    bandpass(src, sr, S.gritF, 1.3);
    applyExpDecay(src, sr, 0.004);
    const d = granular(sr, 0.3, src, rng, { grains: Math.round(14 + 30 * S.grit), grainMs: 10, spread: 0.6, pitchJitter: 0.6, gainJitter: 0.85 });
    for (let i = 0; i < d.length; i++) d[i] *= Math.exp(-(i / sr) / 0.08);
    normArr(d, 1);
    emit(ac, dest, t0 + 0.01, d, v * S.grit * 0.45, [bq(ac, 'highpass', 700, 0.7)]);
  }
  HANDLING.gear(ac, dest, t0 + 0.006, rng, { volume: Math.min(1.6, 0.9 + 1.1 * k) * (opts.volume ?? 1) });
  HANDLING.cloth(ac, dest, t0 + 0.02, rng, { volume: 0.8 * (opts.volume ?? 1) });

  return { duration: 0.6 };
}

/** A slide: sustained friction that the caller can retrigger. */
export function buildSlide(ac, dest, t0, rng, surface = 'concrete', opts = {}) {
  const S = stepSpec(surface);
  const sr = ac.sampleRate;
  const seconds = opts.seconds ?? 0.8;
  const v = (opts.volume ?? 1) * S.level * 1.2;
  const n = Math.floor(sr * seconds);
  const d = noiseSlice('white', n, rng);
  for (let i = 0; i < n; i++) {
    const k = i / n;
    d[i] *= Math.min(1, k * 12) * Math.pow(1 - k, 1.6);
  }
  bandpass(d, sr, S.scuffF * 0.6, 0.6);
  normArr(d, 1);
  const f = bq(ac, 'lowpass', S.scuffF * 1.4, 1.1);
  f.frequency.setValueAtTime(S.scuffF * 1.6, t0);
  f.frequency.exponentialRampToValueAtTime(Math.max(200, S.scuffF * 0.35), t0 + seconds);
  emit(ac, dest, t0, d, v * 0.5, [f]);
  const th = sineSweep(sr, Math.min(0.5, seconds), S.heelF, S.heelF1, 0.10, rng, 2);
  emit(ac, dest, t0, th, v * 0.4, [bq(ac, 'lowpass', 260, 0.9)]);
  HANDLING.gear(ac, dest, t0 + 0.02, rng, { volume: 1.1 * (opts.volume ?? 1) });
  return { duration: seconds + 0.2 };
}
